/* Kora product page: gallery + lightbox (<kora-gallery>) and variant/buy logic (<kora-product>). No dependencies. */
(() => {
  const K = (window.Kora = window.Kora || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const smooth = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

  /* ---------- gallery ---------- */
  class KoraGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      if (!this.track) return;
      this.slides = $$('[data-media-id]', this.track);
      this.dots = $$('[data-dot]', this);
      this.thumbs = $$('[data-thumb]', this);
      this.dialog = $('dialog', this);
      this.index = 0;
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.goToIndex(i)));
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goToIndex(i)));
      this.track.addEventListener('keydown', (e) => {
        if (!this.scrollable() || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
        e.preventDefault();
        this.goToIndex(Math.max(0, Math.min(this.slides.length - 1, this.index + (e.key === 'ArrowRight' ? 1 : -1))));
      });
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-zoom-open]');
        if (z) this.openZoom(this.zoomables().indexOf(z.closest('[data-media-id]')), z);
      });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          if (!this.scrollable()) return;
          entries.forEach((en) => { if (en.isIntersecting) this.mark(this.slides.indexOf(en.target)); });
        }, { root: this.track, threshold: 0.6 });
        this.slides.forEach((s) => this.io.observe(s));
      }
      if (this.dialog) this.initZoom();
      this.mark(0);
      const initial = this.dataset.initial && this.slideIndex(this.dataset.initial);
      if (initial > 0) requestAnimationFrame(() => this.goToIndex(initial, 'auto'));
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
    scrollable() { return this.track.scrollWidth > this.track.clientWidth + 2; }
    slideIndex(id) { return this.slides.findIndex((s) => s.dataset.mediaId === String(id)); }
    mark(i) {
      if (i < 0) return;
      this.index = i;
      this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
    }
    goToIndex(i, behavior) {
      const slide = this.slides[i];
      if (!slide) return;
      if (this.scrollable()) this.track.scrollTo({ left: slide.offsetLeft, behavior: behavior || smooth() });
      else slide.scrollIntoView({ behavior: behavior || smooth(), block: 'center' });
      this.mark(i);
    }
    goTo(mediaId) { const i = this.slideIndex(mediaId); if (i > -1) this.goToIndex(i); }

    zoomables() { return this.slides.filter((s) => s.dataset.zoomSrc); }
    initZoom() {
      this.zImg = $('[data-zoom-img]', this.dialog);
      this.zCount = $('[data-zoom-count]', this.dialog);
      this.zPrev = $('[data-zoom-prev]', this.dialog);
      this.zNext = $('[data-zoom-next]', this.dialog);
      this.zPrev.addEventListener('click', () => this.stepZoom(-1));
      this.zNext.addEventListener('click', () => this.stepZoom(1));
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.stepZoom(-1);
        else if (e.key === 'ArrowRight') this.stepZoom(1);
      });
      this.dialog.addEventListener('close', () => {
        const s = this.zoomables()[this.zi];
        if (s) this.goToIndex(this.slides.indexOf(s), 'auto');
      });
    }
    openZoom(i, trigger) {
      if (i < 0 || !this.dialog) return;
      this.zi = i;
      this.showZoom();
      K.openDialog(this.dialog, trigger);
    }
    stepZoom(d) {
      const n = this.zoomables().length;
      this.zi = (this.zi + d + n) % n;
      this.showZoom();
    }
    showZoom() {
      const list = this.zoomables();
      const s = list[this.zi];
      this.zImg.src = s.dataset.zoomSrc;
      this.zImg.alt = s.dataset.zoomAlt || '';
      this.zCount.textContent = `${this.zi + 1} / ${list.length}`;
      this.zPrev.hidden = this.zNext.hidden = list.length < 2;
    }
  }
  if (!customElements.get('kora-gallery')) customElements.define('kora-gallery', KoraGallery);

  /* ---------- product (variants, quantity, sticky bar) ---------- */
  class KoraProduct extends HTMLElement {
    connectedCallback() {
      this.req = 0;
      const data = $('[data-variants]', this);
      try { this.variants = data ? JSON.parse(data.textContent) : []; } catch (e) { this.variants = []; }
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) this.onOption();
        else if (e.target.matches('[data-plan]')) this.setParam('selling_plan', e.target.value);
      });
      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty-step]');
        if (step) return this.stepQty(step);
        const copy = e.target.closest('[data-copy-link]');
        if (copy) this.copyLink(copy);
      });
      this.initSticky();
      this.initStickyColumns();
      this.remember();
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.colRO) this.colRO.disconnect();
      window.removeEventListener('resize', this.colUpdate);
    }

    /* Desktop: keep the shorter column pinned while the taller one scrolls, until the layout ends.
       A column taller than the viewport pins by its bottom edge so its end is always reachable. */
    initStickyColumns() {
      const cols = [...this.querySelectorAll('[data-sticky-col]')];
      if (!cols.length) return;
      const mq = window.matchMedia('(min-width: 990px)');
      const gap = 24;
      this.colUpdate = () => {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        const stuck = document.querySelector('.header--sticky') ? header : 0;
        cols.forEach((col) => {
          if (!mq.matches) { col.style.position = ''; col.style.top = ''; return; }
          const available = window.innerHeight - stuck - gap * 2;
          const top = col.offsetHeight <= available ? stuck + gap : window.innerHeight - col.offsetHeight - gap;
          col.style.position = 'sticky';
          col.style.top = `${Math.round(top)}px`;
        });
      };
      this.colUpdate();
      window.addEventListener('resize', this.colUpdate, { passive: true });
      if ('ResizeObserver' in window) {
        this.colRO = new ResizeObserver(this.colUpdate);
        cols.forEach((c) => this.colRO.observe(c));
      }
    }

    /* quantity stepper */
    stepQty(btn) {
      const input = $('[data-qty]', btn.closest('.qty'));
      if (!input) return;
      const inc = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const next = (Number(input.value) || min) + Number(btn.dataset.qtyStep) * inc;
      input.value = Math.min(max, Math.max(min, next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* variant selection */
    onOption() {
      const groups = $$('[data-option]', this);
      const values = groups.map((g) => { const el = $('input:checked, select', g); return el ? el.value : null; });
      groups.forEach((g, i) => { const out = $('[data-selected-value]', g); if (out) out.textContent = values[i]; });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === values[i]));
      if (!variant) return this.setBuyState(null);
      const id = $('[data-variant-id]', this);
      if (id) { id.value = variant.id; id.dispatchEvent(new Event('change', { bubbles: true })); }
      this.setParam('variant', variant.id);
      const gallery = $('kora-gallery', this);
      if (gallery && variant.media) gallery.goTo(variant.media);
      this.setBuyState(variant.available);
      document.dispatchEvent(new CustomEvent('product:variant-change', { detail: { variantId: variant.id, productId: this.dataset.productId } }));
      this.refresh(variant.id);
    }
    setParam(name, value) {
      if (this.dataset.updateUrl === undefined) return;
      try {
        const url = new URL(location.href);
        if (value) url.searchParams.set(name, value); else url.searchParams.delete(name);
        history.replaceState(history.state, '', url);
      } catch (e) { /* ignore */ }
    }
    /* available: true | false | null (combination does not exist) */
    setBuyState(available) {
      const label = available === null ? K.strings.unavailable : available ? K.strings.addToCart : K.strings.soldOut;
      $$('[data-add-button], [data-sticky-add]', this).forEach((b) => { b.disabled = !available; });
      $$('[data-add-label]', this).forEach((l) => { l.textContent = label; });
      const pay = $('[data-payment]', this);
      if (pay) pay.hidden = !available;
      if (available === null) K.announce(label);
    }
    async refresh(variantId) {
      const token = ++this.req;
      const plan = $('[data-plan]:checked', this);
      const planValue = plan ? plan.value : '';
      try {
        const doc = await K.fetchSection(`${this.dataset.url}?variant=${variantId}`, this.dataset.sectionId);
        if (token !== this.req) return;
        $$('[data-swap]', doc).forEach((n) => {
          const cur = $(`[data-swap="${n.dataset.swap}"]`, this);
          if (cur) cur.replaceWith(n);
        });
        $$('[data-option-input][id]', doc).forEach((n) => {
          const cur = document.getElementById(n.id);
          if (!cur || !this.contains(cur)) return;
          cur.toggleAttribute('data-unavailable', n.hasAttribute('data-unavailable'));
          if (n.tagName === 'OPTION') cur.textContent = n.textContent;
        });
        if (planValue) { const p = $(`[data-plan][value="${planValue}"]`, this); if (p) p.checked = true; }
        const pay = $('[data-payment]', this);
        const btn = $('[data-add-button]', this);
        if (pay && btn) pay.hidden = btn.disabled;
      } catch (e) { /* keep optimistic state */ }
    }

    /* sticky bar (mobile): visible once the main buy button has scrolled out of view upwards */
    initSticky() {
      const bar = $('[data-sticky-bar]', this);
      const anchor = $('[data-buy-anchor]', this);
      if (!bar || !anchor || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(([en]) => {
        const show = !en.isIntersecting && en.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', show);
        bar.inert = !show;
      });
      this.io.observe(anchor);
    }

    copyLink(btn) {
      const label = $('[data-copy-label]', btn);
      const done = () => {
        if (!label) return;
        const old = label.textContent;
        label.textContent = btn.dataset.copied;
        K.announce(btn.dataset.copied);
        setTimeout(() => { label.textContent = old; }, 2000);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(btn.dataset.url).then(done, () => {});
    }

    /* recently viewed: keep the last product ids in localStorage */
    remember() {
      try {
        const key = 'kora:recent';
        const id = this.dataset.productId;
        const list = JSON.parse(localStorage.getItem(key) || '[]').filter((x) => String(x) !== id);
        list.unshift(id);
        localStorage.setItem(key, JSON.stringify(list.slice(0, 20)));
      } catch (e) { /* storage unavailable */ }
    }
  }
  if (!customElements.get('kora-product')) customElements.define('kora-product', KoraProduct);
})();
